import { useEffect } from 'react'

/**
 * Reusable Collapsible Modal Component
 * Implements the GJYL signature glassmorphism effect with:
 * - Backdrop blur
 * - Smooth fade-and-slide animations
 * - Multiple close mechanisms (X button, backdrop click, ESC key)
 * - Layered modal support (modals can stack)
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'default', // 'small', 'default', 'large', 'full'
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  noPadding = false, // For layouts that need full control (like sidebar layouts)
}) {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, closeOnEscape, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const sizeClasses = {
    small: 'max-w-md',
    default: 'max-w-2xl',
    large: 'max-w-4xl',
    full: 'max-w-6xl',
  }

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <>
      {/* Backdrop - visual only */}
      <div className="modal-backdrop"></div>

      {/* Modal Container */}
      <div className="modal-container">
        {/* Clickable overlay to close modal when clicking outside content */}
        {closeOnBackdrop && (
          <div
            className="absolute inset-0 pointer-events-auto"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />
        )}
        <div
          className={`modal-content ${sizeClasses[size]} w-full`}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-6 border-b border-gjyl-slate-700/50">
              {title && (
                <h2 className="text-xl font-semibold text-white">{title}</h2>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gjyl-slate-700/50 rounded-lg transition-colors group ml-auto"
                  aria-label="Close modal"
                >
                  <svg
                    className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div
            className={noPadding ? "overflow-hidden" : "p-6 overflow-y-auto scrollbar-gjyl"}
            style={noPadding ? { height: 'calc(90vh - 80px)' } : { maxHeight: 'calc(90vh - 140px)' }}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Layered Modal Component
 * For sub-settings that slide over the main modal
 */
export function LayeredModal({
  isOpen,
  onClose,
  title,
  children,
  parentModalRef,
}) {
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Dimmed overlay for parent modal */}
      <div className="fixed inset-0 bg-black/40 z-50 animate-fade-in"></div>

      {/* Layered Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div
          className="glass-strong rounded-2xl shadow-gjyl-lift max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slide-in"
          style={{
            transform: 'translateX(0)',
            animation: 'slideInFromRight 0.3s ease-out',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gjyl-slate-700/50">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gjyl-slate-700/50 rounded-lg transition-colors group"
              aria-label="Go back"
            >
              <svg
                className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 className="text-xl font-semibold text-white flex-1 text-center">{title}</h2>
            <div className="w-9"></div> {/* Spacer for centering */}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto scrollbar-gjyl" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            {children}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(50px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  )
}
